import {
  elementNameToPhpClassName,
  phpReservedWords
} from "@/lib/elements/studio/element-to-php";
import NotificationLogger from "@/lib/notifications/notifications";
import {
  ElementStudioSaveLocation,
  SaveLocationType
} from "@/types/element-studio";
import { Control, ControlType } from "@/types/controls";

export function getItemNamespaceFromSlug(slug: string) {
  const parts = slug.split("\\");

  if (parts.length === 2) {
    return parts[0];
  } else {
    NotificationLogger.log.message(
      `slug "${slug}" did not contain a namespace and a name`
    );
  }

  return "";
}

export function getItemNameFromSlug(slug: string) {
  const parts = slug.split("\\");

  if (parts.length === 2) {
    return parts[1];
  } else {
    NotificationLogger.log.message(
      `slug "${slug}" did not contain a namespace and a name`
    );
  }

  return "";
}

export function getElementSlug(namespace: string, elementName: string) {
  return `${namespace}\\${elementNameToPhpClassName(elementName)}`;
}

export function booleanToString(bool: boolean) {
  return bool ? "true" : "false";
}

export function isValidSlug(slug: string) {
  return slug && slug.includes("\\");
}

export function isValidItemName(newName: string) {
  const name = elementNameToPhpClassName(newName);

  if (!name) {
    return "Name cannot be empty";
  }

  // The name becomes the className and a reserved word will make PHP explode
  if (phpReservedWords.includes(name)) {
    return "You're using a PHP reserved word as a name";
  }

  const includesOnlyTextNumberAndUnderscore = /^[a-zA-Z0-9 _-]*$/.test(name);
  if (!includesOnlyTextNumberAndUnderscore) {
    return 'Only letters, numbers, spaces, hyphens "-", and underscore "_" are allowed';
  }

  return true;
}

export function makeValuePhpValid(maybeString: unknown) {
  if (typeof maybeString === "string") {
    // escape single quote to prevent syntax error
    return `'${maybeString.replace(/'/g, "\\'")}'`;
  } else if (typeof maybeString === "boolean") {
    return booleanToString(maybeString);
  } else if (typeof maybeString === "number") {
    return maybeString;
  } else if (maybeString === null) {
    return "null";
  } else if (maybeString === undefined) {
    return undefined;
  } else {
    console.warn("unexpected value encountered in wrapIfString", maybeString);
    // todo: really? what types could the value of a controlOption be?
    return "'" + String(maybeString) + "' /* unexpected value encountered */ ";
  }
}

export function logNotificationOfSameItem(
  saveLocations: ElementStudioSaveLocation[],
  newItem: ElementStudioSaveLocation & { name: string },
  itemType: SaveLocationType
) {
  const locationName = saveLocations.find(
    location => location.directoryPath === newItem.directoryPath
  )?.label;

  NotificationLogger.log.message(
    `Another ${itemType} with the name "${newItem.name}" in ${
      `"${locationName}"` || "the same location"
    }" already exists.`
  );
}

export function getPathToAllControls(
  controls: Control[],
  controlTypes: ControlType[] = [],
  controlTypesToIgnore: ControlType[] = ["section"],
  parentPath: string
) {
  const addPathToAccumulator = (
    control: Control,
    parentPath: string,
    acc: string[]
  ) => {
    const currentPath = `${parentPath ? `${parentPath}.` : ""}${control.slug}`;

    if (
      !controlTypesToIgnore.includes(control.options.type) &&
      (controlTypes.includes(control.options.type) || !controlTypes.length)
    ) {
      acc.push(currentPath);
    }

    control.children.forEach(control => {
      acc = addPathToAccumulator(control, currentPath, acc);
    });

    return acc;
  };

  const paths: string[] = [];

  controls.forEach(control => {
    paths.concat(addPathToAccumulator(control, parentPath, paths));
  });

  return paths;
}
